/**
 * Login Page - Clean Grras Solutions Style with Slider
 */
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2 } from 'lucide-react';
import SEO from '../components/SEO';

const Login = () => {
  const [credentials, setCredentials] = useState({ 
    email: '', 
    password: ''
  });
  const [error, setError] = useState('');
  const errorRef = useRef('');
  const { login, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && errorRef.current) {
      setError(errorRef.current);
      errorRef.current = '';
    }
  }, [loading]);

  // Course carousel data WITH original company logos
  const courses = [
    { 
      name: "Red Hat Enterprise Linux", 
      description: "Master the world's leading enterprise OS",
      details: "Get RHCSA & RHCE certified with hands-on training on real RHEL systems",
      features: ["Expert Trainers", "Hands-on Labs", "Job Assistance"],
      badge: "95% Pass Rate",
      color: "from-red-500 to-red-600",
      icon: "🐧",
      logo: "https://cdn.jsdelivr.net/npm/simple-icons@v9/icons/redhat.svg"
    },
    {
      name: "Amazon Web Services",
      description: "Cloud computing dominance starts here", 
      details: "AWS Solutions Architect & DevOps certifications with live cloud labs",
      features: ["Expert Trainers", "Hands-on Labs", "Job Assistance"],
      badge: "500+ AWS Certified",
      color: "from-orange-500 to-orange-600",
      icon: "☁️",
      logo: "https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg"
    },
    {
      name: "Kubernetes Orchestration", 
      description: "Orchestrate the future of applications",
      details: "Master K8s, container orchestration, and cloud-native technologies",
      features: ["Expert Trainers", "Hands-on Labs", "Job Assistance"],
      badge: "CKA/CKAD Ready",
      color: "from-blue-500 to-blue-600",
      icon: "⚙️",
      logo: "https://upload.wikimedia.org/wikipedia/commons/3/39/Kubernetes_logo_without_workmark.svg"
    },
    {
      name: "Docker Containerization",
      description: "Package applications for any environment", 
      details: "Container fundamentals, Docker Compose, and production deployment strategies",
      features: ["Expert Trainers", "Hands-on Labs", "Job Assistance"],
      badge: "Container Expert",
      color: "from-cyan-500 to-cyan-600",
      icon: "🐳",
      logo: "https://www.docker.com/wp-content/uploads/2022/03/vertical-logo-monochromatic.png"
    },
    {
      name: "GitHub & DevOps",
      description: "Collaborate like a professional developer",
      details: "Git workflows, GitHub Actions, CI/CD pipelines and collaborative development",
      features: ["Expert Trainers", "Hands-on Labs", "Job Assistance"],
      badge: "Industry Standard", 
      color: "from-gray-600 to-gray-700",
      icon: "🔧",
      logo: "https://github.githubassets.com/images/modules/logos_page/GitHub-Mark.png"
    }
  ];

  const [currentCourse, setCurrentCourse] = useState(0);
  const [logoErrors, setLogoErrors] = useState({});

  // Auto-rotate courses
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentCourse((prev) => (prev + 1) % courses.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [courses.length]);

  const handleChange = (e) => {
    setCredentials(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!credentials.email || !credentials.password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      await login({ 
        email: credentials.email, 
        password: credentials.password 
      });
      navigate('/dashboard');
    } catch (error) {
      const msg = error?.message || '';
      const lmsg = msg.toLowerCase();
      let errorMsg;
      if (error instanceof TypeError || lmsg.includes('failed to fetch') || lmsg.includes('network error')) {
        errorMsg = '⚠️ Connection error. Please check your internet and try again.';
      } else if (lmsg.includes('too many') || lmsg.includes('locked') || lmsg.includes('rate') || lmsg.includes('blocked')) {
        errorMsg = `🔒 ${msg}`;
      } else if (lmsg.includes('session expired') || lmsg.includes('invalid') || lmsg.includes('incorrect') || lmsg.includes('wrong') || lmsg.includes('unauthorized')) {
        errorMsg = '❌ Incorrect email or password. Please try again.';
      } else {
        errorMsg = 'Something went wrong. Please try again.';
      }
      errorRef.current = errorMsg;
      setError(errorMsg);
    }
  };

  const course = courses[currentCourse];

  return (
    <>
      <SEO 
        title="Login - DevOps Lab Platform | Grras Solutions"
        description="Create a free account to practice Linux, DevOps, Docker and Kubernetes on real hands-on lab environments. Upgrade anytime."
        keywords="DevOps login, Docker labs, Kubernetes training, Ansible automation, Linux lab platform, container management"
      />
      <div className="min-h-screen bg-gray-50 flex">
      
      {/* Left Panel - Login Form - Responsive */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-md">
          
          {/* Header */}
          <div className="text-center mb-8">
            <p className="text-gray-600">
              IT Training & Certification Institute
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Since 2007 • 3000+ Certified Professionals
            </p>
          </div>

          {/* Welcome Back */}
          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">
              Welcome Back
            </h2>
            <p className="text-gray-600">
                Sign in to access hands-on labs for Linux, DevOps & Programming
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg">
                <p className="text-red-400 text-sm font-medium">{error}</p>
              </div>
            )}
            
            <div>
              <Label htmlFor="email" className="text-gray-700 font-medium">Email Address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={credentials.email}
                onChange={handleChange}
                className="mt-2 border-gray-300 focus:border-red-500 focus:ring-red-500 py-3 text-base touch-manipulation"
                placeholder="Enter your email address"
                data-testid="login-email-input"
              />
            </div>
            
            <div>
              <Label htmlFor="password" className="text-gray-700 font-medium">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={credentials.password}
                onChange={handleChange}
                className="mt-2 border-gray-300 focus:border-red-500 focus:ring-red-500 py-3 text-base touch-manipulation"
                placeholder="Enter your password"
                data-testid="login-password-input"
              />
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center">
                <input type="checkbox" className="rounded border-gray-300 text-red-600 focus:ring-red-500" />
                <span className="ml-2 text-gray-600">Remember me </span>
              </label>
              <Link to="/forgot-password" className="text-red-600 hover:text-red-700 font-medium">
                Forgot password?
              </Link>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-4 text-base min-h-[48px] touch-manipulation"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign In & Continue →
            </Button>
          </form>
          
          <div className="text-center mt-8 text-sm">
            <span className="text-gray-600">New to Grras Solutions? </span>
            <Link 
              to="/register" 
              className="text-blue-600 hover:text-blue-700 font-medium"
              data-testid="register-link"
            >
              Create Account
            </Link>
            <p className="text-xs text-gray-500 mt-2">
              Create a free account to start with free Linux practice labs
            </p>
          </div>

          {/* Trust Indicators */}
          <div className="flex items-center justify-center space-x-6 mt-8 text-xs text-gray-500">
            <div className="flex items-center space-x-1">
              <span>🔒</span>
              <span>Secure Login</span>
            </div>
            <div className="flex items-center space-x-1">
              <span>⭐</span>
              <span>ISO Certified</span>
            </div>
            <div className="flex items-center space-x-1">
              <span>✅</span>
              <span>Trusted Platform</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Course Carousel with Original Logos */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <div className={`absolute inset-0 bg-gradient-to-br ${course.color} flex items-center justify-center text-white transition-all duration-1000`}>
          <div className="text-center px-8 max-w-lg">
            
            {/* Course Logo */}
            <div className="mb-8">
              <div className="w-28 h-28 mx-auto bg-white rounded-2xl p-4 mb-4 shadow-lg">
                {logoErrors[currentCourse] ? (
                  <div className="w-full h-full flex items-center justify-center text-4xl">
                    {course.icon}
                  </div>
                ) : (
                  <img 
                    src={course.logo} 
                    alt={`${course.name} logo`}
                    className="w-full h-full object-contain filter brightness-100 contrast-125"
                    onError={() => {
                      setLogoErrors(prev => ({
                        ...prev,
                        [currentCourse]: true
                      }));
                    }}
                  />
                )}
              </div>
              
              <div className="inline-block bg-white bg-opacity-20 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                {course.badge}
              </div>
            </div>

            {/* Course Content */}
            <h2 className="text-3xl font-bold mb-4">
              {course.name}
            </h2>
            <p className="text-xl mb-4 opacity-90">
              {course.description}
            </p>
            <p className="text-base mb-8 opacity-80 leading-relaxed">
              {course.details}
            </p>

            {/* Course Features */}
            <div className="flex justify-center space-x-6 text-sm mb-8">
              {course.features.map((feature, index) => (
                <div key={index} className="flex items-center space-x-1">
                  <span>✓</span>
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            {/* Carousel Indicators */}
            <div className="flex justify-center space-x-2">
              {courses.map((_, index) => (
                <button
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentCourse ? 'bg-white' : 'bg-white bg-opacity-40'
                  }`}
                  onClick={() => setCurrentCourse(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Login;
